// Persistent notification log for the in-app bell (components/NotificationCenter).
//
// Every alert that goes through fireNotification (order chats, direct messages,
// group rooms, forwarded SMS, new emails, order-assigned) also lands here, so
// missing the 5-second toast no longer means missing the event. The list is
// kept per user in localStorage and read back on sign-in — a reload, a browser
// close/reopen, and moving to a different tab all still see the same history.
//
// This is a plain vanilla store (module-scoped state + listeners) rather than
// a React context so fireNotification can push from module scope without
// threading callbacks through every listener effect in NotificationHandler.
// Components consume it via useSyncExternalStore (see the hook below).
import { useSyncExternalStore } from 'react';
import type { NotificationData } from './notificationRouting';

export type NotificationKind = 'chat' | 'sms' | 'email' | 'order' | 'room' | 'other';

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  data: NotificationData;
  /** Deep-link the panel row navigates to on click. */
  url?: string;
  kind: NotificationKind;
  /** Stable de-duplication key from buildNotificationTag. An unread row with
   *  the same tag gets replaced by the newer body instead of stacking. */
  tag: string;
  createdAt: number;
  read: boolean;
}

const MAX_ITEMS = 100;
const listeners = new Set<() => void>();
let currentUserId: string | null = null;
let items: NotificationItem[] = [];

const storageKey = (uid: string) => `wolf_notifCenter_${uid}`;

const emit = () => { for (const l of listeners) l(); };

const load = () => {
  if (!currentUserId) { items = []; return; }
  try {
    const raw = localStorage.getItem(storageKey(currentUserId));
    const parsed = raw ? JSON.parse(raw) : [];
    items = Array.isArray(parsed) ? parsed.slice(0, MAX_ITEMS) : [];
  } catch { items = []; }
};

const persist = () => {
  if (!currentUserId) return;
  try {
    localStorage.setItem(storageKey(currentUserId), JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch { /* private mode / quota */ }
};

/** Called on sign-in / sign-out so the log follows the account, not the tab. */
export const setNotificationUser = (uid: string | null) => {
  if (uid === currentUserId) return;
  currentUserId = uid;
  load();
  emit();
};

const kindFromData = (data: NotificationData): NotificationKind => {
  switch (data.type) {
    case 'room_message':
    case 'room_reaction':
      return 'room';
    case 'forwarded_sms':
      return 'sms';
    case 'email':
      return 'email';
    case 'order_assigned':
    case 'order_unassigned':
    case 'order_deleted':
    case 'order_pickup_ready':
    case 'order_edited':
    case 'order_delivered':
    case 'order_marked_delivered': // 0105: the office marked the driver's order delivered
      return 'order';
    case 'direct_message':
    case 'direct_message_reaction':
    case 'new_message':
    case 'message_reaction':
      return 'chat';
    default:
      return 'other';
  }
};

export interface AddInput {
  title: string;
  body: string;
  data: NotificationData;
  url?: string;
  tag: string;
}

export const addNotification = (input: AddInput) => {
  if (!currentUserId) return;
  const now = Date.now();
  const item: NotificationItem = {
    id: `${now}_${Math.random().toString(36).slice(2, 8)}`,
    title: input.title,
    body: input.body,
    data: input.data,
    url: input.url,
    kind: kindFromData(input.data),
    tag: input.tag,
    createdAt: now,
    read: false,
  };
  // Dedupe: an unread row already keyed by this tag gets replaced with the
  // fresher body (5 messages in one order chat → 1 row, not 5). Read history
  // is preserved so returning to the panel after a chat burst still shows the
  // earlier read entries in order.
  const dupIdx = input.tag ? items.findIndex(i => i.tag === input.tag && !i.read) : -1;
  items = (dupIdx >= 0
    ? [item, ...items.filter((_, idx) => idx !== dupIdx)]
    : [item, ...items]
  ).slice(0, MAX_ITEMS);
  persist();
  emit();
};

export const markNotificationRead = (id: string) => {
  const target = items.find(i => i.id === id);
  if (!target || target.read) return;
  items = items.map(i => i.id === id ? { ...i, read: true } : i);
  persist();
  emit();
};

export const markAllNotificationsRead = () => {
  if (items.every(i => i.read)) return;
  items = items.map(i => i.read ? i : { ...i, read: true });
  persist();
  emit();
};

// ── Chat rows follow the chat's read state (2026-09-24) ──────────────────────────────────────
// The bell used to be its own island: reading a chat cleared the chat badge but left its bell row
// unread until clicked. The database read state (user_read_state → currentUser.lastReadChat) is
// the one source of truth; these apply it to the chat rows. Other kinds (email, orders, stock)
// are never touched.
const CHAT_TYPES = new Set(['new_message', 'message_reaction', 'direct_message', 'direct_message_reaction']);
/** Read times can come from another device; allow its clock a little drift. */
export const READ_CLOCK_SKEW_MS = 2000;

const chatThread = (d: NotificationData | undefined): { conv: string; channel: string } | null => {
  if (!d || !CHAT_TYPES.has(d.type)) return null;
  if (d.type === 'direct_message' || d.type === 'direct_message_reaction') {
    return d.conversationId ? { conv: d.conversationId, channel: 'dispatch' } : null; // direct chats read on 'dispatch'
  }
  return d.orderId ? { conv: d.orderId, channel: d.channel || 'dispatch' } : null;
};

const markWhere = (isRead: (i: NotificationItem, t: { conv: string; channel: string }) => boolean) => {
  let changed = false;
  const next = items.map((i) => {
    if (i.read) return i;
    const t = chatThread(i.data);
    if (!t || !isRead(i, t)) return i;
    changed = true;
    return { ...i, read: true };
  });
  if (!changed) return;
  items = next;
  persist();
  emit();
};

/** This user just read a chat thread here (one channel, or all): its bell rows so far are read. */
export const markChatNotificationsRead = (conv: string, channel?: string, upTo: number = Date.now()) =>
  markWhere((i, t) => t.conv === conv && (!channel || t.channel === channel) && i.createdAt <= upTo + READ_CLOCK_SKEW_MS);

/** Read times synced from the database (any tab or device): rows for threads read since they arrived are read. */
export const reconcileChatNotifications = (lastReadChat: Record<string, Record<string, number>> | undefined) => {
  if (!lastReadChat) return;
  markWhere((i, t) => {
    const read = lastReadChat[t.conv]?.[t.channel];
    return !!read && i.createdAt <= read + READ_CLOCK_SKEW_MS;
  });
};

export const removeNotification = (id: string) => {
  if (!items.some(i => i.id === id)) return;
  items = items.filter(i => i.id !== id);
  persist();
  emit();
};

export const clearAllNotifications = () => {
  if (items.length === 0) return;
  items = [];
  persist();
  emit();
};

export const getNotifications = (): NotificationItem[] => items;
export const getUnreadCount = (): number => {
  let n = 0;
  for (const i of items) if (!i.read) n += 1;
  return n;
};

export const subscribeNotifications = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => { listeners.delete(l); };
};

// getSnapshot must return the SAME reference between renders when nothing
// changed — every mutation above reassigns `items` to a fresh array, so this
// stable identity check is the store's diffing signal for useSyncExternalStore.
export const useNotificationCenter = (): { items: NotificationItem[]; unread: number } => {
  const list = useSyncExternalStore(subscribeNotifications, getNotifications, getNotifications);
  const unread = useSyncExternalStore(subscribeNotifications, getUnreadCount, getUnreadCount);
  return { items: list, unread };
};
